import { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Loader2, X } from 'lucide-react';

import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import CompactSelectField from '../../../../components/Forms/Input/CompactSelectField';
import Checkbox from '../../../../components/Forms/Input/Checkbox';
import ActionButton from '../../../../components/Buttons/ActionButton';

import { RestaurantDiningRoomDTO } from '../../../../types/restaurant/RestaurantDiningRoomTypes';
import {
  RestaurantOrderItemDTO,
  RestaurantOrderSummaryDTO,
} from '../../../../types/restaurant/RestaurantOrderTypes';
import { formatOrderMoney, orderItemName } from '../orderPresentation';

interface Props {
  open: boolean;
  rooms: RestaurantDiningRoomDTO[];
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onLoadItems: (filters: {
    search?: string;
    movableOnly?: boolean;
  }) => Promise<RestaurantOrderItemDTO[]>;
  onLoadTargets: (filters: {
    diningRoomId?: number;
    search?: string;
  }) => Promise<RestaurantOrderSummaryDTO[]>;
  onConfirm: (targetOrderId: number, itemIds: number[]) => Promise<unknown> | void;
}

const OrderMoveItemsModal = ({
  open,
  rooms,
  busy,
  onOpenChange,
  onLoadItems,
  onLoadTargets,
  onConfirm,
}: Props) => {
  const [itemSearch, setItemSearch] = useState('');
  const [debouncedItemSearch, setDebouncedItemSearch] = useState('');
  const [items, setItems] = useState<RestaurantOrderItemDTO[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const [roomId, setRoomId] = useState<number | 'ALL'>('ALL');
  const [targetSearch, setTargetSearch] = useState('');
  const [debouncedTargetSearch, setDebouncedTargetSearch] = useState('');
  const [targets, setTargets] = useState<RestaurantOrderSummaryDTO[]>([]);
  const [targetId, setTargetId] = useState<number | null>(null);
  const [loadingTargets, setLoadingTargets] = useState(false);

  useEffect(() => {
    if (open) {
      setItemSearch('');
      setDebouncedItemSearch('');
      setSelectedItems([]);
      setRoomId('ALL');
      setTargetSearch('');
      setDebouncedTargetSearch('');
      setTargetId(null);
    }
  }, [open]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedItemSearch(itemSearch), 300);
    return () => clearTimeout(handle);
  }, [itemSearch]);

  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedTargetSearch(targetSearch),
      300
    );
    return () => clearTimeout(handle);
  }, [targetSearch]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingItems(true);
    onLoadItems({
      search: debouncedItemSearch.trim() || undefined,
      movableOnly: true,
    })
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingItems(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedItemSearch, onLoadItems]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingTargets(true);
    onLoadTargets({
      diningRoomId: roomId === 'ALL' ? undefined : roomId,
      search: debouncedTargetSearch.trim() || undefined,
    })
      .then((data) => {
        if (!cancelled) setTargets(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingTargets(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, roomId, debouncedTargetSearch, onLoadTargets]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onOpenChange, open]);

  const roomOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Todos los salones' },
      ...rooms.map((room) => ({ value: room.id, label: room.name })),
    ],
    [rooms]
  );

  const toggleItem = (id: number) =>
    setSelectedItems((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id]
    );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[8999] flex items-center justify-center bg-black/60 px-3 py-6 backdrop-blur-[2px]"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-move-items-title"
        className="flex max-h-[88vh] w-full max-w-xl flex-col rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
      >
        <header className="flex items-center justify-between border-b border-stroke px-5 py-4 dark:border-strokedark">
          <div>
            <h2
              id="order-move-items-title"
              className="font-semibold text-black dark:text-white"
            >
              Mover productos a otra orden
            </h2>
            <p className="mt-1 text-xs text-bodydark2">
              Solo se pueden mover productos que aún no fueron enviados a cocina.
            </p>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-2 text-bodydark2 transition hover:bg-gray-2 dark:hover:bg-meta-4"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div>
            <p className="mb-2 text-sm font-semibold text-black dark:text-white">
              Productos ({selectedItems.length} seleccionados)
            </p>
            <SearchInputField
              name="moveItemSearch"
              value={itemSearch}
              placeholder="Buscar producto de esta orden..."
              withMargin={false}
              size="sm"
              onChange={(event) => setItemSearch(event.target.value)}
            />
            <div className="mt-2 max-h-[26vh] space-y-1.5 overflow-y-auto pr-1">
              {loadingItems ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-stroke px-3 py-2 text-sm dark:border-strokedark"
                    >
                      <Checkbox
                        id={`move-item-${item.id}`}
                        label={`${orderItemName(item)} · x${item.quantity}`}
                        checked={selectedItems.includes(item.id)}
                        onChange={() => toggleItem(item.id)}
                      />
                      <span className="shrink-0 text-bodydark2">
                        {formatOrderMoney(item.subtotal ?? item.total)}
                      </span>
                    </div>
                  ))}
                  {!items.length && (
                    <p className="py-6 text-center text-sm text-bodydark2">
                      No hay productos disponibles para mover.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-black dark:text-white">
              Orden destino
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <CompactSelectField
                label="Salón"
                name="moveTargetRoom"
                value={roomId}
                withMargin={false}
                options={roomOptions}
                onChange={(event) =>
                  setRoomId(
                    event.target.value === 'ALL'
                      ? 'ALL'
                      : Number(event.target.value)
                  )
                }
              />
              <SearchInputField
                label="Buscar orden"
                name="moveTargetSearch"
                value={targetSearch}
                placeholder="Mesa u orden..."
                withMargin={false}
                size="sm"
                onChange={(event) => setTargetSearch(event.target.value)}
              />
            </div>

            <div className="mt-2 max-h-[24vh] space-y-1.5 overflow-y-auto pr-1">
              {loadingTargets ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {targets.map((target) => (
                    <button
                      key={target.id}
                      type="button"
                      onClick={() => setTargetId(target.id)}
                      className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                        targetId === target.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-stroke hover:border-primary/40 hover:bg-primary/5 dark:border-strokedark'
                      }`}
                    >
                      <span>
                        {target.tableName || target.orderNumber}
                        {target.tableCode ? ` · ${target.tableCode}` : ''}
                        {target.diningRoomName ? (
                          <span className="ml-1 text-xs text-bodydark2">
                            ({target.diningRoomName})
                          </span>
                        ) : null}
                      </span>
                      <span className="shrink-0 text-xs text-bodydark2">
                        {target.activeItemCount ?? 0} ítems ·{' '}
                        {formatOrderMoney(target.total ?? 0)}
                      </span>
                    </button>
                  ))}
                  {!targets.length && (
                    <p className="py-6 text-center text-sm text-bodydark2">
                      No hay otras órdenes activas con esos filtros.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <footer className="grid grid-cols-2 gap-3 border-t border-stroke p-4 dark:border-strokedark">
          <ActionButton
            label="Cancelar"
            variant="outline"
            size="md"
            fullWidth
            onClick={() => onOpenChange(false)}
          />
          <ActionButton
            label="Mover productos"
            icon={<ArrowRightLeft className="h-4 w-4" />}
            variant="primary"
            size="md"
            fullWidth
            loading={busy}
            disabled={!targetId || !selectedItems.length || busy}
            onClick={async () => {
              if (targetId && selectedItems.length) {
                await onConfirm(targetId, selectedItems);
              }
            }}
          />
        </footer>
      </section>
    </div>
  );
};

export default OrderMoveItemsModal;
